import { Loader2, Workflow } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorMessage } from '@/i18n'
import { BOARD_PRIORITIES } from '../boards/api'
import { MATCH_BY_EVENT, RULE_EVENTS, type RuleEvent, type RuleInput, useRuleMutations } from './api'
import { type RuleLookups, slugify } from './ruleLookups'

/** Olaya göre başlıkta kullanılabilecek değişkenler. */
function placeholdersFor(event: RuleEvent, matchType: string) {
  const out = ['{account}']
  if (matchType === 'each_module' || matchType === 'module') out.push('{module}')
  if (event === 'sales.stage_changed' || event === 'install.stage_changed') out.push('{stage}')
  if (event === 'meeting.planned') out.push('{meeting_kind}')
  if (event === 'demo.expiring') out.push('{days}')
  return out
}

type Props = { id?: string; initial: RuleInput; lookups: RuleLookups; onClose: () => void }

/**
 * Kural düzenleyici dört soruyla ilerler: ne zaman, hangi durumda, hangi görev, nereye ve kime. Pano, kolon, aşama,
 * modül ve görüşme türü sistemden seçilir; teknik kodlar kullanıcıya gösterilmez.
 */
export function RuleEditorModal({ id, initial, lookups, onClose }: Props) {
  const { t } = useTranslation()
  const { save } = useRuleMutations()
  const [rule, setRule] = useState<RuleInput>(initial)
  const [submitted, setSubmitted] = useState(false)
  const set = <K extends keyof RuleInput>(k: K, v: RuleInput[K]) => setRule((r) => ({ ...r, [k]: v }))

  const matchTypes = MATCH_BY_EVENT[rule.event]
  const needsValue = rule.matchType !== 'always' && rule.matchType !== 'each_module'
  const board = lookups.boards.find((b) => b.code === rule.boardCode) ?? lookups.boards[0]
  const valueOptions = useMemo(() => lookups.matchOptions(rule.event, rule.matchType, rule.matchValue), [lookups, rule.event, rule.matchType, rule.matchValue])
  const errors = {
    group: submitted && rule.event === 'manual' && !rule.matchValue.trim() ? t('projects.rules.errors.group') : undefined,
    value: submitted && rule.event !== 'manual' && needsValue && !rule.matchValue.trim() ? t('projects.rules.errors.value') : undefined,
    title: submitted && !rule.title.trim() ? t('projects.rules.errors.title') : undefined,
  }

  const onEvent = (event: RuleEvent) =>
    setRule((r) => ({ ...r, event, matchType: MATCH_BY_EVENT[event][0] ?? 'always', matchValue: '' }))
  const insert = (token: string) => set('title', `${rule.title}${rule.title && !rule.title.endsWith(' ') ? ' ' : ''}${token}`)

  const submit = () => {
    setSubmitted(true)
    if (errors.group || errors.value || errors.title || !rule.title.trim()) return
    // Görev türü motorun bir olayda aynı görevi iki kez açmamasını sağlar; yeni kuralda başlıktan türetilir.
    // "Her modül için" kuralında tür modül adını içermeli; yoksa motor tüm modüller için tek görev açar.
    let taskType = rule.taskType.trim() || slugify(rule.title)
    if (rule.matchType === 'each_module' && !taskType.includes('{module}')) taskType = `{module}_${taskType}`.slice(0, 80)
    const input = { ...rule, title: rule.title.trim(), taskType, boardCode: board?.code ?? rule.boardCode }
    save.mutate(
      { id, input },
      {
        onSuccess: () => {
          toast.success(t('projects.rules.saved'))
          onClose()
        },
        onError: (e) => toast.error(apiErrorMessage(e)),
      },
    )
  }

  return (
    <Modal
      open
      size="xl"
      onOpenChange={(v) => !v && onClose()}
      dismissible={!save.isPending}
      icon={<Workflow />}
      title={t(id ? 'projects.rules.editTitle' : 'projects.rules.add')}
      description={t('projects.rules.editorHint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={
        <>
          <label className="mr-auto flex items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 accent-primary" checked={rule.isActive} onChange={(e) => set('isActive', e.target.checked)} />
            {t('projects.rules.f.active')}
          </label>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Step n={1} title={t('projects.rules.steps.when')}>
          <Field label={t('projects.rules.f.event')} hint={t(`projects.rules.eventHints.${rule.event}`)}>
            <Select value={rule.event} onChange={(v) => onEvent(v as RuleEvent)} options={RULE_EVENTS.map((x) => ({ value: x, label: t(`projects.rules.events.${x}`) }))} />
          </Field>
          {rule.event === 'manual' ? (
            <Field label={t('projects.rules.f.group')} hint={t('projects.rules.hints.group')} error={errors.group}>
              <Select
                searchable
                creatable
                value={rule.matchValue}
                onChange={(v) => set('matchValue', v)}
                options={lookups.groups.map((g) => ({ value: g, label: g }))}
                placeholder={t('projects.rules.f.groupPlaceholder')}
                invalid={!!errors.group}
              />
            </Field>
          ) : null}
        </Step>

        {rule.event !== 'manual' ? (
          <Step n={2} title={t('projects.rules.steps.condition')}>
            <Field label={t('projects.rules.f.match')}>
              <Select value={rule.matchType} onChange={(v) => setRule((r) => ({ ...r, matchType: v as RuleInput['matchType'], matchValue: '' }))} options={matchTypes.map((m) => ({ value: m, label: t(`projects.rules.match.${m}`) }))} />
            </Field>
            {needsValue ? (
              <Field label={t(`projects.rules.matchValue.${rule.matchType}`)} error={errors.value}>
                <Select
                  searchable
                  creatable={rule.matchType === 'deliverable'}
                  value={rule.matchValue}
                  onChange={(v) => set('matchValue', v)}
                  options={valueOptions}
                  placeholder={t('projects.rules.pick')}
                  invalid={!!errors.value}
                />
              </Field>
            ) : null}
          </Step>
        ) : null}

        <Step n={rule.event === 'manual' ? 2 : 3} title={t('projects.rules.steps.task')}>
          <Field label={t('projects.rules.f.title')} error={errors.title} wide>
            <Input value={rule.title} maxLength={255} placeholder={t('projects.rules.f.titlePlaceholder')} onChange={(e) => set('title', e.target.value)} aria-invalid={!!errors.title} />
            <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-muted-foreground">
              <span>{t('projects.rules.insertVar')}</span>
              {placeholdersFor(rule.event, rule.matchType).map((p) => (
                <button key={p} type="button" onClick={() => insert(p)} className="rounded-md border bg-muted/40 px-1.5 py-0.5 font-mono text-[11px] text-foreground hover:bg-muted">
                  {t(`projects.rules.vars.${p.slice(1, -1)}`)}
                </button>
              ))}
            </div>
          </Field>
          <Field label={t('projects.rules.f.description')} wide>
            <Textarea rows={3} value={rule.description} onChange={(e) => set('description', e.target.value)} />
          </Field>
          <Field label={t('projects.rules.f.priority')}>
            <Select value={rule.priority} onChange={(v) => set('priority', v)} options={BOARD_PRIORITIES.map((p) => ({ value: p, label: t(`projects.rules.priority.${p}`) }))} />
          </Field>
          <Field label={t('projects.rules.f.due')} hint={t('projects.rules.hints.due')}>
            <Input inputMode="numeric" placeholder={t('projects.rules.noDue')} value={rule.dueInDays ?? ''} onChange={(e) => set('dueInDays', e.target.value.trim() === '' ? null : Number(e.target.value.replace(/\D/g, '')) || 0)} />
          </Field>
        </Step>

        <Step n={rule.event === 'manual' ? 3 : 4} title={t('projects.rules.steps.where')}>
          <Field label={t('projects.rules.f.board')}>
            <Select
              value={board?.code ?? rule.boardCode}
              onChange={(code) => {
                const next = lookups.boards.find((b) => b.code === code)
                setRule((r) => ({ ...r, boardCode: code, columnKey: next?.columns[0]?.key ?? r.columnKey }))
              }}
              options={lookups.boards.map((b) => ({ value: b.code, label: b.name, description: b.description }))}
            />
          </Field>
          <Field label={t('projects.rules.f.column')} hint={t('projects.rules.hints.column')}>
            <Select value={rule.columnKey} onChange={(v) => set('columnKey', v)} options={(board?.columns ?? []).map((c) => ({ value: c.key, label: c.name }))} />
          </Field>
          <Field label={t('projects.rules.f.role')} hint={t('projects.rules.hints.role')} wide>
            <Select
              searchable
              creatable
              value={rule.assigneeRole}
              onChange={(v) => set('assigneeRole', v.slice(0, 60))}
              options={[{ value: '', label: t('projects.rules.defaultAssignee') }, ...lookups.roles.map((r) => ({ value: r, label: r }))]}
              placeholder={t('projects.rules.f.rolePlaceholder')}
            />
          </Field>
        </Step>
      </div>
    </Modal>
  )
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <span className="flex size-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{n}</span>
        {title}
      </h3>
      <div className="grid grid-cols-1 gap-3 pl-0 sm:pl-8 md:grid-cols-2">{children}</div>
    </section>
  )
}

function Field({ label, hint, error, wide, children }: { label: string; hint?: string; error?: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={wide ? 'space-y-1.5 md:col-span-2' : 'space-y-1.5'}>
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}
